/**
 * The collector's ceilings.
 *
 * ── 🔴 Why this screen shows a SOURCE beside every number ─────────────────
 *
 * Concurrency and batch size can come from three places: the `platform_settings`
 * row, the collector's environment, or `CYCLE_DEFAULTS`. A screen that showed
 * the effective value alone would be unable to answer the only question an
 * operator has when a change does not take — *is this screen the thing that
 * decides?* A value sourced `environment` is one this screen is currently NOT
 * deciding, and saying so is the difference between a settings page and a
 * settings-shaped page.
 *
 * That is the same failure this repository keeps finding in its own documents:
 * something that reads as though it already did the thing. A number in a box
 * reads as configuration whether or not anything downstream is reading it.
 *
 * ── Clearing is a real operation ─────────────────────────────────────────
 *
 * Empty is not zero. Emptying a field clears the stored value and hands the
 * setting back to the environment, and the source badge moves to prove it
 * happened. Without that there is no way back once this screen has taken over a
 * field — you could only overwrite it with a number you had to guess.
 *
 * ── Not everyone may be here ─────────────────────────────────────────────
 *
 * One collector serves every tenant, so raising these spends a budget the
 * person raising them cannot see the rest of. The API refuses anyone who is not
 * platform staff with a completed MFA session; this screen says why rather than
 * rendering an editor that will 403 on save, because a control that looks
 * usable and is not is worse than an explanation.
 */

import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from './api.ts';
import {
  Banner,
  Button,
  DataTable,
  Empty,
  Field,
  Loading,
  Pill,
  Section,
  Toast,
  type ColumnDef,
} from './components/index.ts';

type Source = 'platform' | 'environment' | 'default';

interface Settings {
  stored: { concurrency: number | null; batchSize: number | null };
  effective: { concurrency: number; batchSize: number; leaseSeconds: number };
  sources: { concurrency: Source; batchSize: Source };
  updatedAt: string | null;
  updatedByName: string | null;
}

interface Pass {
  id: string;
  worker_id: string;
  started_at: string;
  duration_ms: number | null;
  outcome: string;
  error: string | null;
  interval_ms: number;
  batch_size: number;
  concurrency: number;
  claimed: number | null;
  completed: number | null;
  still_due: number | null;
  oldest_due_age_s: number | null;
}

interface ReconcileRun {
  id: string;
  recorder_key: string;
  started_at: string;
  outcome: string;
  backup_set: number | null;
  registered: number | null;
  unregistered: number | null;
  not_writing: number | null;
  error: string | null;
}

interface Runs {
  collector: { passes: Pass[]; worstRatio: number | null; warnAtRatio: number; failures: number };
  reconcile: { runs: ReconcileRun[]; failures: number; notWriting: number };
}

const SOURCE_LABEL: Record<Source, string> = {
  platform: 'set here',
  environment: "from the collector's environment",
  default: 'built-in default',
};

const SOURCE_REASON: Record<Source, string> = {
  platform: 'Stored on the platform row. This screen decides the value.',
  environment: 'Set in the collector’s environment. This screen is not deciding it; enter a number to take over.',
  default: 'Nothing stored and nothing in the environment; the built-in default applies.',
};

/**
 * The source, as a pill. `platform` is the only one that means this screen is
 * in charge, so it is the only one that reads as active.
 */
function SourcePill({ source }: { source: Source }): React.JSX.Element {
  return (
    <div>
      <Pill state={source === 'platform' ? 'ok' : 'neutral'} reason={SOURCE_REASON[source]}>
        {SOURCE_LABEL[source]}
      </Pill>
    </div>
  );
}

/**
 * "18.6s of 120s · 16%" — the shape an operator can act on.
 *
 * ⚠️ Sub-second values stay in milliseconds. Rounding to seconds rendered a
 * 1 ms test interval as "of 0s", which reads as a divide-by-zero rather than as
 * the very short interval it is — and a screen that looks broken on odd data is
 * a screen nobody trusts on ordinary data.
 */
function passLoad(p: Pass): string {
  if (p.duration_ms === null || p.interval_ms <= 0) return '—';
  const pct = Math.round((p.duration_ms / p.interval_ms) * 100);
  const unit = (ms: number): string => (ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`);
  return `${unit(p.duration_ms)} of ${unit(p.interval_ms)} · ${pct}%`;
}

function when(iso: string): string {
  const then = new Date(iso).getTime();
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  return hrs < 24 ? `${hrs}h ago` : `${Math.round(hrs / 24)}d ago`;
}

const PASS_COLUMNS: ColumnDef<Pass, unknown>[] = [
  {
    id: 'started',
    header: 'Started',
    accessorKey: 'started_at',
    cell: ({ getValue }) => when(getValue() as string),
  },
  {
    id: 'load',
    header: 'Load',
    accessorFn: (p) => (p.duration_ms === null || p.interval_ms <= 0 ? -1 : p.duration_ms / p.interval_ms),
    meta: { align: 'right' },
    cell: ({ row }) => passLoad(row.original),
  },
  {
    id: 'claimed',
    header: 'Claimed',
    accessorFn: (p) => p.claimed ?? -1,
    meta: { align: 'right' },
    cell: ({ row }) => (
      <>
        {row.original.claimed ?? '—'}
        {row.original.completed !== null &&
          row.original.claimed !== null &&
          row.original.completed !== row.original.claimed && (
            <span className="muted"> · {row.original.completed} done</span>
          )}
      </>
    ),
  },
  // still_due is the one that compounds: a pass that leaves work behind hands
  // it to the next one, and the queue grows rather than drains.
  {
    id: 'backlog',
    header: 'Backlog',
    accessorFn: (p) => p.still_due ?? 0,
    meta: { align: 'right' },
    cell: ({ row }) =>
      row.original.still_due === null || row.original.still_due === 0 ? (
        <span className="muted">clear</span>
      ) : (
        <Pill state="warning" reason="Work this pass left behind is handed to the next one; the queue grows rather than drains.">
          {row.original.still_due} left
        </Pill>
      ),
  },
  {
    id: 'ceilings',
    header: 'Ceilings it ran under',
    accessorFn: (p) => `batch ${p.batch_size} · concurrency ${p.concurrency}`,
    enableSorting: false,
    cell: ({ getValue }) => <span className="muted small">{getValue() as string}</span>,
  },
  {
    id: 'outcome',
    header: 'Outcome',
    accessorKey: 'outcome',
    cell: ({ row }) => (
      <Pill
        state={row.original.outcome === 'ok' ? 'ok' : row.original.outcome === 'running' ? 'neutral' : 'fault'}
        reason={row.original.error}
      >
        {row.original.outcome}
      </Pill>
    ),
  },
];

const RECONCILE_COLUMNS: ColumnDef<ReconcileRun, unknown>[] = [
  {
    id: 'started',
    header: 'Started',
    accessorKey: 'started_at',
    cell: ({ getValue }) => when(getValue() as string),
  },
  {
    id: 'recorder',
    header: 'Recorder',
    accessorKey: 'recorder_key',
    cell: ({ getValue }) => <span className="muted">{getValue() as string}</span>,
  },
  {
    id: 'set',
    header: 'In the set',
    accessorFn: (r) => r.backup_set ?? -1,
    meta: { align: 'right' },
    cell: ({ row }) => row.original.backup_set ?? '—',
  },
  {
    id: 'registered',
    header: 'Registered',
    accessorFn: (r) => r.registered ?? -1,
    meta: { align: 'right' },
    cell: ({ row }) => row.original.registered ?? '—',
  },
  {
    id: 'notWriting',
    header: 'Not writing',
    accessorFn: (r) => r.not_writing ?? 0,
    meta: { align: 'right' },
    cell: ({ row }) =>
      row.original.not_writing === null || row.original.not_writing === 0 ? (
        <span className="muted">0</span>
      ) : (
        <Pill state="fault" reason="Registered and producing nothing — looks configured from every other angle.">
          {row.original.not_writing}
        </Pill>
      ),
  },
  {
    id: 'outcome',
    header: 'Outcome',
    accessorKey: 'outcome',
    cell: ({ row }) => (
      <Pill state={row.original.outcome === 'ok' ? 'ok' : 'fault'} reason={row.original.error}>
        {row.original.outcome}
      </Pill>
    ),
  },
];

export function SettingsView({
  isPlatformAdmin,
}: {
  isPlatformAdmin: boolean;
}): React.JSX.Element {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [runs, setRuns] = useState<Runs | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [concurrency, setConcurrency] = useState('');
  const [batchSize, setBatchSize] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [next, activity] = await Promise.all([
        api.get<Settings>('/api/platform/settings'),
        /*
         * Fetched beside the ceilings because it is how you read whether a
         * ceiling change worked. Failure here must not blank the editor above,
         * so it degrades to null rather than throwing the whole load.
         */
        api.get<Runs>('/api/platform/runs').catch(() => null),
      ]);
      setSettings(next);
      setRuns(activity);
      // The inputs show what is STORED, not what is effective. Seeding them
      // with the effective value would silently offer to write the
      // environment's number into the database on the next save, and the two
      // would then be the same by accident rather than by anyone deciding.
      setConcurrency(next.stored.concurrency === null ? '' : String(next.stored.concurrency));
      setBatchSize(next.stored.batchSize === null ? '' : String(next.stored.batchSize));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'could not read the platform settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isPlatformAdmin) void load();
    else setLoading(false);
  }, [isPlatformAdmin, load]);

  if (!isPlatformAdmin) {
    return (
      <Banner tone="info" title="These settings belong to the platform, not to this tenant">
        One collector serves every tenant, so its concurrency and batch size are a shared budget.
        Platform staff with multi-factor authentication can change them. Everything that belongs
        to this tenant — alert routing, notification channels, users and site access — is on its
        own tab.
      </Banner>
    );
  }

  if (loading) return <Loading rows={3} cols={2} label="Loading the platform settings" />;
  if (error !== null) return <Banner tone="fault">{error}</Banner>;
  if (settings === null) return <Loading rows={3} cols={2} label="Loading the platform settings" />;

  /** Empty means clear; anything else must be a positive integer. */
  const parse = (raw: string): number | null | 'invalid' => {
    if (raw.trim() === '') return null;
    const n = Number(raw);
    return Number.isInteger(n) && n > 0 ? n : 'invalid';
  };

  const parsedConcurrency = parse(concurrency);
  const parsedBatch = parse(batchSize);
  const invalid = parsedConcurrency === 'invalid' || parsedBatch === 'invalid';
  const dirty =
    parsedConcurrency !== settings.stored.concurrency || parsedBatch !== settings.stored.batchSize;

  async function save(): Promise<void> {
    if (invalid || settings === null) return;
    setSaving(true);
    try {
      await api.put('/api/platform/settings', {
        concurrency: parsedConcurrency as number | null,
        batchSize: parsedBatch as number | null,
      });
      // Re-read rather than trusting what was sent: the source badges are
      // derived server-side, and a toast must confirm what the screen already
      // shows rather than be the only evidence.
      await load();
      setToast('The collector picks these up on its next pass.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'could not save');
    } finally {
      setSaving(false);
    }
  }

  /*
   * 🔴 The ratio, not the duration. A pass that overruns its interval books
   * its own lateness as camera downtime — the uptime rollup reads any gap over
   * 3x cadence as a device being down — so how close to the edge the collector
   * runs is the number to read BEFORE raising a ceiling above. The threshold
   * comes from the server so this screen and the daemon cannot disagree about
   * what "close" means.
   */
  const worst = runs?.collector.worstRatio ?? null;
  const hot = worst !== null && runs !== null && worst >= runs.collector.warnAtRatio;

  return (
    <div className="stack">
      <Section
        title="Collector ceilings"
        summary={`Platform-wide: one collector serves every tenant. Raise concurrency before batch size — a large batch worked by too few workers churns rather than finishes. Lease is ${settings.effective.leaseSeconds}s and not settable here.`}
      >
        <div className="form-narrow">
          <Field
            id="concurrency"
            label="Concurrency"
            {...(parsedConcurrency === 'invalid'
              ? {}
              : {
                  hint: `In effect: ${settings.effective.concurrency}. Leave empty to use the collector's environment.`,
                })}
            error={parsedConcurrency === 'invalid' ? 'A positive whole number, or empty to clear.' : null}
          >
            <input
              id="concurrency"
              inputMode="numeric"
              value={concurrency}
              placeholder={String(settings.effective.concurrency)}
              onChange={(e) => setConcurrency(e.target.value)}
            />
            <SourcePill source={settings.sources.concurrency} />
          </Field>

          <Field
            id="batchSize"
            label="Batch size"
            {...(parsedBatch === 'invalid'
              ? {}
              : { hint: `In effect: ${settings.effective.batchSize}. Claimed per tenant, per pass.` })}
            error={parsedBatch === 'invalid' ? 'A positive whole number, or empty to clear.' : null}
          >
            <input
              id="batchSize"
              inputMode="numeric"
              value={batchSize}
              placeholder={String(settings.effective.batchSize)}
              onChange={(e) => setBatchSize(e.target.value)}
            />
            <SourcePill source={settings.sources.batchSize} />
          </Field>

          <div className="row">
            <Button variant="primary" busy={saving} disabled={!dirty || invalid} onClick={() => void save()}>
              Save
            </Button>
            {dirty && !saving && (
              <Button
                variant="ghost"
                onClick={() => {
                  setConcurrency(
                    settings.stored.concurrency === null ? '' : String(settings.stored.concurrency),
                  );
                  setBatchSize(settings.stored.batchSize === null ? '' : String(settings.stored.batchSize));
                }}
              >
                Discard
              </Button>
            )}
            <span className="spacer" />
            {settings.updatedAt !== null && (
              <span className="small muted">
                Last changed by {settings.updatedByName ?? 'someone since deleted'}
              </span>
            )}
          </div>
        </div>
      </Section>

      {runs !== null && (
        <>
          {hot && worst !== null && (
            <Banner tone="warn" title={`Busiest recent pass used ${Math.round(worst * 100)}% of its interval`}>
              At or past the point where a pass starts booking its own lateness as downtime. Read
              this before raising a ceiling.
            </Banner>
          )}
          <Section
            flush
            title="Collector passes"
            summary={
              `${runs.collector.passes.length} most recent` +
              (runs.collector.failures > 0 ? ` · ${runs.collector.failures} did not finish cleanly` : '') +
              (worst !== null && !hot
                ? ` · busiest used ${Math.round(worst * 100)}% of its interval (the collector warns itself at ${Math.round(runs.collector.warnAtRatio * 100)}%)`
                : '')
            }
          >
            <DataTable
              data={runs.collector.passes}
              columns={PASS_COLUMNS}
              getRowId={(p) => p.id}
              initialSort={[{ id: 'started', desc: true }]}
              rowClass={(p) => (p.outcome !== 'ok' && p.outcome !== 'running' ? 'row-fault' : undefined)}
              minWidth={720}
              caption="Recent collector passes"
              empty={<Empty title="No collector pass has been recorded" />}
            />
          </Section>

          {/*
            🔴 `not_writing` is the number this whole reconciler exists to find:
            a backup that is REGISTERED and writing nothing looks configured from
            every other angle, which is precisely why nothing else catches it.
          */}
          {runs.reconcile.notWriting > 0 && (
            <Banner tone="fault" title={`${runs.reconcile.notWriting} registered backup(s) are not writing`}>
              Registered and producing nothing looks configured from every other angle.
            </Banner>
          )}
          <Section
            flush
            title="Recorder reconcile"
            summary={
              `${runs.reconcile.runs.length} most recent` +
              (runs.reconcile.failures > 0 ? ` · ${runs.reconcile.failures} failed` : '')
            }
          >
            <DataTable
              data={runs.reconcile.runs}
              columns={RECONCILE_COLUMNS}
              getRowId={(r) => r.id}
              initialSort={[{ id: 'started', desc: true }]}
              rowClass={(r) => (r.outcome !== 'ok' ? 'row-fault' : undefined)}
              minWidth={640}
              caption="Recent recorder reconcile runs"
              empty={
                /*
                 * ⚠️ "Nothing has run" is a finding, not a blank. The reconciler
                 * is what separates "on no recorder" from "nothing has looked",
                 * so a console that showed an empty table under a silent header
                 * would be making exactly the mistake the reconciler exists to
                 * prevent.
                 */
                <Empty
                  title="No reconcile pass has been recorded"
                  body="The reconciler runs on the recorder host every ten minutes. Nothing here means it has not run, not that every backup is healthy."
                />
              }
            />
          </Section>
        </>
      )}

      {toast !== null && <Toast message={toast} tone="ok" onDismiss={() => setToast(null)} />}
    </div>
  );
}
